import React, {useMemo} from "react";
import {Button, Checkbox, FormControlLabel, Grid, Stack, Typography} from "@mui/material";
import {HibobResidence, OfficeForm} from "../../api/Model";
import {mapArraySetState, mapSetState, SetState, switchChange} from "../../util/FormHandlers";
import {SiteSelect} from "../../components/site-select/SiteSelect";
import HibobOfficeSelect from "../../components/hibob-office-select/HibobOfficeSelect";
import {deleteElementByIndex} from "../../util/ArrayUtil";
import ClearIcon from "@mui/icons-material/Clear";

function ResidenceRow({index, residence, setForm}: {
    index: number,
    residence: HibobResidence,
    setForm: SetState<OfficeForm>,
}) {
    const {setSite, setHibobOffice, deleteResidence} = useMemo(() => {
        const setResidenceArray = mapSetState(setForm, 'hibobResidence');
        const setResidence = mapArraySetState(setResidenceArray, index);
        return {
            setSite: mapSetState(setResidence, 'workSite'),
            setHibobOffice: mapSetState(setResidence, 'hibobOffice'),
            deleteResidence: () =>
                setResidenceArray(prevState => deleteElementByIndex(prevState, index)),
        };
    }, [setForm, index]);

    return (
        <Grid spacing={1} container wrap="nowrap" alignItems="center">
            <Grid size="grow" minWidth={0}>
                <SiteSelect
                    label="Work Site"
                    site={residence.workSite}
                    setSite={setSite}
                    fullWidth
                />
            </Grid>
            <Grid size="grow" minWidth={0}>
                <HibobOfficeSelect
                    label="HiBob Office"
                    fullWidth
                    value={residence.hibobOffice}
                    onChange={setHibobOffice}
                />
            </Grid>
            <Grid size="auto">
                <Button aria-label="Remove mapping" onClick={deleteResidence} sx={{width: 32, height: 32, minWidth: 32, p: 0}}>
                    <ClearIcon/>
                </Button>
            </Grid>
        </Grid>
    );
}

export function OfficeFormDialog({form, setForm}: {
    form: OfficeForm,
    setForm: SetState<OfficeForm>,
}) {
    const handlers = useMemo(() => {
        const setResidenceArray = mapSetState(setForm, 'hibobResidence');
        return ({
            addResidenceClick: () => setResidenceArray(prevState => [
                ...prevState,
                {workSite: null, hibobOffice: null}
            ]),
            changeCheckInsIntegration: switchChange(setForm, enabled => ({checkInsIntegrationEnabled: enabled})),
            changeReleezmeIntegration: switchChange(setForm, enabled => ({releezmeIntegrationEnabled: enabled})),
        });
    }, [setForm]);

    return (
        <Stack spacing={1} direction="column" sx={{pt: 2}}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={form.checkInsIntegrationEnabled}
                        onChange={handlers.changeCheckInsIntegration}
                    />
                }
                label="Check-Ins App integration is active"
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={form.releezmeIntegrationEnabled}
                        onChange={handlers.changeReleezmeIntegration}
                    />
                }
                label="VECOS Releezme integration is active"
            />

            {form.hibobResidence.map((residence, index) => (
                <ResidenceRow
                    key={index}
                    index={index}
                    residence={residence}
                    setForm={setForm}
                />
            ))}
            <Button onClick={handlers.addResidenceClick}>Add Envoy to Hibob office mapping</Button>
            <Typography variant="body2">
                To be considered a resident of the Envoy office, employees must match every selected field in
                at least one row. Select a Hibob work site, a HiBob office, or both.
            </Typography>
        </Stack>
    );
}
